import React, { useEffect, useState } from 'react'
import { useIntl, FormattedMessage } from 'react-intl'
import SlideBase from './SlideBase'
import Caption from 'LuxKit/Typography/Caption'
import Group from 'components/utils/Group'
import Heading from 'LuxKit/Typography/Heading'
import FormatDecimal from 'components/utils/Formatters/FormatDecimal'
import FadeBackground from '../FadeBackground'
import { useWrappedStorySelector } from 'contexts/Wrapped'
import { WRAPPED_SLIDES } from '../../config/slides'
import useDistanceDisplay from 'hooks/useDistanceDisplay'
import BodyText from 'LuxKit/Typography/BodyText'
import useVideoBackgroundWithFallback from '../../hooks/useVideoBackgroundWithFallback'

// Get slide config for this slide (static, doesn't depend on runtime data)
const SLIDE_CONFIG = WRAPPED_SLIDES.find(s => s.id === 'distance')
const SLIDE_ID = 'distance' as const

function DistanceSlide() {
  const intl = useIntl()
  // Use granular selectors to minimize re-renders
  const data = useWrappedStorySelector(state => state.data)
  const totalsData = useWrappedStorySelector(state => state.totalsData)

  // Use the video background with fallback hook
  const { background, isBackgroundReady, isCurrentSlide } = useVideoBackgroundWithFallback({
    slideConfig: SLIDE_CONFIG,
    slideId: SLIDE_ID,
  })

  const [animate, setAnimate] = useState(!SLIDE_CONFIG?.video) // If no video, animate immediately

  // Totals-only mode is determined by not having user wrapped data
  const isTotalsOnlyMode = !data
  const hasTravelled = !!(data?.stats.citiesVisited?.length && data.stats.totalDistanceKm > 0)
  const useTotalsFallback = isTotalsOnlyMode || !hasTravelled
  const totalDistanceKm = totalsData?.stats.totalDistanceKm ?? 0
  const travelledDistanceKm = useTotalsFallback ? totalDistanceKm : (data?.stats.totalDistanceKm ?? 0)
  const { displayDistance, unitLabel } = useDistanceDisplay(travelledDistanceKm)
  const useCompactFormat = displayDistance >= 1_000_000

  // Start animation when background is ready (video loaded or fallback image)
  useEffect(() => {
    if (SLIDE_CONFIG?.video && isBackgroundReady && isCurrentSlide) {
      setAnimate(true)
    }
  }, [isBackgroundReady, isCurrentSlide])

  // Need either user data or totals data to render
  if (!data && !totalsData) return null

  const isFarTraveller = (data?.stats.totalDistanceKm ?? 0) > 200

  // Extract heading variants as constants before return
  const headingTotalsFallback = intl.formatMessage({
    id: 'Rn8GxjMQP/',
    defaultMessage: 'In 2025, Luxury Escapes travellers journeyed',
    description: 'Distance slide heading for totals fallback mode',
  })
  const headingNoTravel = intl.formatMessage({
    id: 'iiwfT1fJjR',
    defaultMessage: 'In 2025, Luxury Escapes travellers journeyed',
    description: 'Distance slide heading when user has not travelled',
  })
  const headingFarTraveller = intl.formatMessage({
    id: '88C0qnXpkx',
    defaultMessage: 'You journeyed far and wide with us',
    description: 'Distance slide heading for far travellers',
  })
  const headingCloseToHome = intl.formatMessage({
    id: '9qOlHc1kXw',
    defaultMessage: 'You proved the best escapes are closest to home',
    description: 'Distance slide heading for travellers who stayed close to home',
  })

  const heading = useTotalsFallback
    ? headingTotalsFallback
    : !hasTravelled
        ? headingNoTravel
        : isFarTraveller
          ? headingFarTraveller
          : headingCloseToHome

  const captionText = intl.formatMessage({
    id: 'Zvmr+SLLRi',
    defaultMessage: 'Total Distance Travelled',
    description: 'Distance slide caption',
  })

  const bodyText = intl.formatMessage({
    id: 'ypMz1110Zr',
    defaultMessage: 'That’s 55 thousand trips around the earth – but less than halfway to Pluto!',
    description: 'Distance slide body text about total distance travelled',
  })

  return (
    <SlideBase background={background}>
      {animate && <FadeBackground>
        <Group direction="vertical" grow verticalAlign="end" horizontalAlign="start" gap={12}>
          <Group direction="vertical" gap={4}>
            <Caption colour="highlight-primary" variant="small" format="uppercase">{captionText}</Caption>
            <Heading variant="heading5"><b>{heading}</b></Heading>
          </Group>
          <Heading variant="heading1">
            <i>
              <FormattedMessage
                id="d3+0uWeOJq"
                defaultMessage="{formatDecimal} {unitLabel}"
                description="Distance slide distance value with FormatDecimal component and unit label"
                values={{
                  formatDecimal: <FormatDecimal value={displayDistance} notation={useCompactFormat ? 'compact' : 'standard'} compactDisplay="long" maximumFractionDigits={0} />,
                  unitLabel,
                }}
              />
            </i>
          </Heading>
          {isTotalsOnlyMode && <BodyText variant="medium">
            {bodyText}
          </BodyText>}
        </Group>
      </FadeBackground>}
    </SlideBase>
  )
}

export default React.memo(DistanceSlide)
